import { Timestamped } from '../timestamped';
import { z } from 'zod';

export type CgPosition = z.infer<typeof CgPosition>;
export type CgMetadata = z.infer<typeof CgMetadata>;
export type CgMetadataHierarchy = z.infer<typeof CgMetadataHierarchy>;
export type CgLabel = z.infer<typeof CgLabel>;
export type CgLabelHierarchy = z.infer<typeof CgLabelHierarchy>;
export type CgNetwork = z.infer<typeof CgNetwork>;
export type CgNetworkHierarchy = z.infer<typeof CgNetworkHierarchy>;
export type CommodityGroup = z.infer<typeof CommodityGroup>;
export type CgHierarchyHeader = z.infer<typeof CgHierarchyHeader>;

export const CgPosition = z.object({
    weight: z.number(),
    x: z.number(),
    y: z.number(),
    systemId: z.number(),
});

export const CgMetadata = z.object({
    icon: z.string().nullish(),
    localizationKey: z.string().nullish(),
    flags: z.array(z.string()).nullish(),
    disableBasketRemoval: z.boolean().nullish(),
    disabledDiscountCodes: z.array(z.string()).nullish(),
    positions: z.array(CgPosition).nullish(),
});

export const CgMetadataHierarchy = z.object({
    fullId: z.string(),
    source: z.enum(['metadata', 'storeMetadataOverrides']),
    depth: z.number(),
    metadata: CgMetadata,
});

export const CgLabel = z.object({
    weight: z.number().nullish(),
    modifiedBy: z.string().nullish(),
    modifiedOn: z.string().nullish(),
});

export const CgLabelHierarchy = z.object({
    fullId: z.string(),
    depth: z.number(),
    labels: z.record(z.string(), CgLabel),
});

export const CgNetwork = z.object({
    modifiedBy: z.string().nullish(),
    modifiedOn: z.string().nullish(),
});

export const CgNetworkHierarchy = z.object({
    fullId: z.string(),
    depth: z.number(),
    classificationNetworks: z.record(z.string(), CgNetwork),
});

export const CommodityGroup = Timestamped.extend({
    fullId: z.string(),
    parentId: z.string().nullish(),
    name: z.string().nullish(),
    inventoryManagementSystem: z.number(),
    isLeaf: z.boolean().nullish(),
    idHierarchy: z.array(z.string()).nullish(),
    nameHierarchy: z.array(z.string()).nullish(),
    metadata: CgMetadata.nullish(),
    labels: z.record(z.string(), CgLabel).nullish(),
    classificationNetworks: z.record(z.string(), CgNetwork).nullish(),
    metadataHierarchy: z.array(CgMetadataHierarchy).nullish(),
    labelHierarchy: z.array(CgLabelHierarchy).nullish(),
    classificationNetworkHierarchy: z.array(CgNetworkHierarchy).nullish(),
});

export const CgHierarchyHeader = z.object({
    inventoryManagementSystem: z.number(),
    hierarchyGroupHeaders: z.array(z.string()),
});
